import { useEffect, useState } from "react";
import clsx from "clsx";
import { listKontaktLieferanten, type Geschaeftspartner } from "@/lib/kontakte";

export function LieferantSearchField({
  label,
  value,
  selectedGpId,
  onChange,
  placeholder = "Lieferant suchen …",
  hint,
  required,
  className,
}: {
  label: string;
  value: string;
  selectedGpId?: number | null;
  onChange: (next: { name: string; gpId: number | null }) => void;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  className?: string;
}) {
  const [searchQ, setSearchQ] = useState(value);
  const [results, setResults] = useState<Geschaeftspartner[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSearchQ(value);
  }, [value]);

  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearchLoading(true);
      void listKontaktLieferanten({ q, limit: 20 })
        .then((res) => setResults(res.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchQ]);

  function selectGp(gp: Geschaeftspartner) {
    onChange({ name: gp.firmenname, gpId: gp.id });
    setSearchQ(gp.firmenname);
    setOpen(false);
  }

  function clearSelection() {
    onChange({ name: "", gpId: null });
    setSearchQ("");
    setOpen(false);
  }

  return (
    <div className={clsx("space-y-1", className)}>
      <label className="block text-sm font-medium text-label">
        {label}
        {required ? <span className="text-status-red"> *</span> : null}
      </label>
      <div className="relative">
        <input
          className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm"
          placeholder={placeholder}
          value={searchQ}
          onChange={(e) => {
            setSearchQ(e.target.value);
            onChange({ name: e.target.value, gpId: selectedGpId ?? null });
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        />
        {selectedGpId || value.trim() ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-label-secondary hover:text-label"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSelection}
          >
            ×
          </button>
        ) : null}
        {open && (searchLoading || results.length > 0 || searchQ.trim().length >= 2) ? (
          <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-ios border border-separator bg-card py-1 shadow-ios-md">
            {searchLoading ? (
              <li className="px-3 py-2 text-xs text-label-secondary">Suche …</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-2 text-xs text-label-secondary">Kein Lieferant gefunden.</li>
            ) : (
              results.map((gp) => (
                <li key={gp.id}>
                  <button
                    type="button"
                    className={clsx(
                      "w-full px-3 py-2 text-left text-sm hover:bg-fill-tertiary/60",
                      selectedGpId === gp.id && "bg-accent/10",
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectGp(gp)}
                  >
                    <span className="font-medium text-label">{gp.firmenname}</span>
                    {gp.ort ? <span className="ml-2 text-xs text-label-secondary">{gp.ort}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {hint ? <p className="text-xs text-label-secondary">{hint}</p> : null}
    </div>
  );
}
